import { organizationSummarySchema, type OrganizationSummary } from '@datum/shared'
import { apiUrl } from './api'

export interface SessionUser {
  id: string
  name: string
  email: string
}

interface AuthEnvelope {
  error?: { message?: string }
}

async function authRequest(path: string, init?: RequestInit): Promise<Response> {
  return fetch(apiUrl(`/api/auth/${path}`), {
    credentials: 'include',
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
}

async function unwrap<T>(response: Response): Promise<T | null> {
  const text = await response.text()
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) {
    // Most often an HTML error/SPA page: the configured API origin no longer
    // serves the API (e.g. after the Worker was renamed in the Cloudflare UI).
    throw new Error(
      `API responded ${response.status} as "${contentType || 'unknown type'}", not JSON. ` +
        'Check that this deployment points at a live /api origin.',
    )
  }
  const data: unknown = text === '' || text === 'null' ? null : JSON.parse(text)
  if (!response.ok) {
    const envelope = (data ?? {}) as AuthEnvelope
    throw new Error(envelope.error?.message ?? `Auth request failed (${response.status})`)
  }
  return (data ?? null) as T | null
}

export async function getSession(): Promise<SessionUser | null> {
  const response = await authRequest('get-session')
  // Better Auth answers 401 when simply signed out; that is "no session", not a failure.
  if (response.status === 401) return null
  const session = await unwrap<{ user?: SessionUser }>(response)
  return session?.user ?? null
}

export interface SignInResult {
  user: SessionUser
  /** False when the server requires email verification first (no session token issued). */
  signedIn: boolean
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const response = await authRequest('sign-in/email', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  const data = await unwrap<{ user: SessionUser; token?: string }>(response)
  if (!data) throw new Error('Sign-in returned no session')
  return { user: data.user, signedIn: typeof data.token === 'string' }
}

export async function signUp(name: string, email: string, password: string): Promise<SignInResult> {
  const response = await authRequest('sign-up/email', {
    method: 'POST',
    body: JSON.stringify({ name, email, password }),
  })
  const data = await unwrap<{ user: SessionUser; token?: string }>(response)
  if (!data) throw new Error('Sign-up returned no session')
  return { user: data.user, signedIn: typeof data.token === 'string' }
}

export async function signOut(): Promise<void> {
  await authRequest('sign-out', { method: 'POST' })
}

export async function listOrganizations(): Promise<OrganizationSummary[]> {
  const data = await unwrap<unknown>(await authRequest('organization/list'))
  const parsed = organizationSummarySchema.array().safeParse(data ?? [])
  return parsed.success ? parsed.data : []
}

export async function createOrganization(name: string, slug: string): Promise<OrganizationSummary> {
  const response = await authRequest('organization/create', {
    method: 'POST',
    body: JSON.stringify({ name, slug }),
  })
  const data = await unwrap<unknown>(response)
  if (!data) throw new Error('Organization creation returned no payload')
  return organizationSummarySchema.parse(data)
}
